/**
 * Adapters for the plan, sale, cashflow and chart contract functions (spread into `registry`).
 * They only translate JSON ⇄ domain values; output shapes mirror ContractRegistry.swift.
 */
import { formatDay } from './core/calendar.ts';
import { appreciateOverWholeMonths } from './calculations/appreciation.ts';
import { buildChartPoints } from './calculations/cash-flow-chart.ts';
import { checkInBalances, checkInDebts, resolveCheckpoint } from './calculations/check-in.ts';
import { calculateCashflow, CASHFLOW_ORDER, type CashflowSeries } from './calculations/cashflow.ts';
import {
  assetEquity, debtBalanceAt, depreciatingAssetEquity, propertyEquity, totalPropertyEquity,
} from './calculations/equity.ts';
import { netWorthTotal, snapshotNetWorth } from './calculations/net-worth.ts';
import { assetSale, propertySale, unifiedAssetSale, type SaleBreakdown } from './calculations/sales.ts';
import {
  decodeAsset, decodeCheckpoint, decodeDepreciatingAsset, decodeFrequency, decodePlan, decodeProperty, decodeTransactions,
  decodeWindow, encodeBucketGroup, encodeWindow,
} from './codec/entities.ts';
import { contextFrom, decodeDay, decodeDecimal, encodeDecimal, encodeDouble, encodeOptionalDecimal } from './codec/scalars.ts';
import { depreciatedValueAfter, remainingEmbeddedFinance } from './models/depreciating-asset.ts';
import { monthlyEquivalent } from './models/frequency.ts';
import {
  debtTransactions, ledgerDebtBalance, linkedMonthlyCost, monthlyAssetCosts, monthlyCostOfOwnership, monthlyDebtPayments,
  monthlyDisposable, monthlyExpenses, monthlyIncome, monthlyInvestments, monthlyPropertyCosts, standaloneDebtBalance,
  debtsCountedInNetWorth, ledgerDebtBalanceCountedInNetWorth, totalAssetValue, totalDepreciatingAssetValue,
} from './models/plan.ts';
import type { Registry } from './registry.ts';
import type { BalanceSnapshot } from './types/domain.ts';
import type * as Wire from './types/contract.generated.ts';

type WireSale = Wire.Functions['CashflowCalculator.propertySale']['expected']['sale'];

const encodeSale = (s: SaleBreakdown | null): WireSale =>
  s === null
    ? null
    : {
        projectedValue: encodeDecimal(s.projectedValue),
        salePrice: encodeDecimal(s.salePrice),
        sellingCosts: encodeDecimal(s.sellingCosts),
        debtCleared: encodeDecimal(s.debtCleared),
        netProceeds: encodeDecimal(s.netProceeds),
      };

const encodeBalances = (b: BalanceSnapshot): Wire.BalancesResult => ({
  cash: encodeDecimal(b.cash),
  investments: encodeDecimal(b.investments),
  savings: encodeDecimal(b.savings),
  debt: encodeDecimal(b.debt),
  propertyEquity: encodeDecimal(b.propertyEquity),
  assetValue: encodeOptionalDecimal(b.assetValue),
  netWorth: encodeDecimal(snapshotNetWorth(b)),
});

const encodeCashflowSeries = (series: CashflowSeries): Wire.BucketGroup =>
  encodeBucketGroup(CASHFLOW_ORDER.map((type) => [type, series[type]] as const));

const uniqueInOrder = (ids: readonly string[]): readonly string[] => [...new Set(ids)];

export const planRegistry: Registry = {
  'NetWorth.total': (input) => ({
    netWorth: encodeDecimal(
      netWorthTotal({
        cash: decodeDecimal(input.cash),
        investments: decodeDecimal(input.investments),
        propertyEquity: decodeDecimal(input.propertyEquity),
        assetEquity: decodeDecimal(input.assetEquity),
        debt: decodeDecimal(input.debt),
      }),
    ),
  }),

  'TransactionFrequency.monthlyEquivalent': (input) => ({
    monthly: encodeDecimal(monthlyEquivalent(decodeFrequency(input.frequency), decodeDecimal(input.amount))),
  }),

  'Appreciation.overWholeMonths': (input) => ({
    value: encodeDecimal(
      appreciateOverWholeMonths(decodeDecimal(input.value), decodeDecimal(input.annualRatePercent), input.months),
    ),
  }),

  'DepreciatingAssetTransaction.depreciatedValue': (input) => {
    const asset = decodeDepreciatingAsset(input.asset);
    return {
      value: encodeDecimal(depreciatedValueAfter(asset, input.monthsOwned)),
      remainingEmbeddedFinance: encodeDecimal(remainingEmbeddedFinance(asset, input.monthsOwned)),
    };
  },

  'CashflowCalculator.calculate': (input) => {
    const result = calculateCashflow(
      {
        transactions: decodeTransactions(input.transactions),
        properties: (input.properties ?? []).map(decodeProperty),
        depreciatingAssets: (input.depreciatingAssets ?? []).map(decodeDepreciatingAsset),
        assets: (input.assets ?? []).map(decodeAsset),
        calculationWindow: decodeWindow(input.window),
        viewWindow: input.viewWindow === undefined || input.viewWindow === null ? null : decodeWindow(input.viewWindow),
      },
      contextFrom(input),
    );
    return {
      calculationWindow: encodeWindow(result.calculationWindow),
      payments: encodeCashflowSeries(result.payments),
      windowed: encodeCashflowSeries(result.windowed),
    };
  },

  'CashflowCalculator.propertySale': (input) => ({
    sale: encodeSale(
      propertySale(
        decodeProperty(input.property), decodeDay(input.saleDate), decodeTransactions(input.linkedDebts), contextFrom(input).now,
      ),
    ),
  }),

  'CashflowCalculator.assetSale': (input) => ({
    sale: encodeSale(
      assetSale(
        decodeDepreciatingAsset(input.asset), decodeDay(input.saleDate), decodeTransactions(input.linkedDebts), contextFrom(input).now,
      ),
    ),
  }),

  'CashflowCalculator.unifiedAssetSale': (input) => ({
    sale: encodeSale(
      unifiedAssetSale(decodeAsset(input.asset), decodeTransactions(input.linkedDebts), contextFrom(input).now),
    ),
  }),

  'Plan.monthlySummary': (input) => {
    const plan = decodePlan(input.plan);
    return {
      monthlyIncome: encodeDecimal(monthlyIncome(plan)),
      monthlyExpenses: encodeDecimal(monthlyExpenses(plan)),
      monthlyDebtPayments: encodeDecimal(monthlyDebtPayments(plan)),
      monthlyInvestments: encodeDecimal(monthlyInvestments(plan)),
      monthlyPropertyCosts: encodeDecimal(monthlyPropertyCosts(plan)),
      monthlyAssetCosts: encodeDecimal(monthlyAssetCosts(plan)),
      monthlyDisposable: encodeDecimal(monthlyDisposable(plan)),
      standaloneDebtIds: debtsCountedInNetWorth(plan).map((debt) => debt.id),
      standaloneDebtBalance: encodeDecimal(standaloneDebtBalance(plan)),
      costOfOwnership: plan.assets.map((asset) => ({
        id: asset.id,
        monthly: encodeDecimal(monthlyCostOfOwnership(plan, asset.id)),
      })),
    };
  },

  'Plan.equity': (input) => {
    const plan = decodePlan(input.plan);
    const date = decodeDay(input.date);
    const ctx = contextFrom(input);
    return {
      totalPropertyEquity: encodeDecimal(totalPropertyEquity(plan, date, ctx)),
      totalDepreciatingAssetEquity: encodeDecimal(totalDepreciatingAssetValue(plan, date)),
      totalAssetValue: encodeDecimal(totalAssetValue(plan, date)),
      assetEquity: plan.assets.map((a) => ({ id: a.id, equity: encodeDecimal(assetEquity(plan, a, date, ctx)) })),
      propertyEquity: plan.properties.map((p) => ({ id: p.id, equity: encodeDecimal(propertyEquity(plan, p, date, ctx)) })),
      depreciatingAssetEquity: plan.depreciatingAssets.map((a) => ({
        id: a.id,
        equity: encodeDecimal(depreciatingAssetEquity(plan, a, date, ctx)),
      })),
      debtBalances: debtTransactions(plan).map((d) => ({ id: d.id, balance: encodeDecimal(debtBalanceAt(d, date, ctx)) })),
    };
  },

  'Plan.linkedMonthlyCost': (input) => {
    const plan = decodePlan(input.plan);
    const ids = uniqueInOrder([...plan.assets, ...plan.properties, ...plan.depreciatingAssets].map((a) => a.id));
    return { linkedMonthlyCost: ids.map((id) => ({ id, monthly: encodeDecimal(linkedMonthlyCost(plan, id)) })) };
  },

  'Plan.ledgerDebtBalance': (input) => {
    const plan = decodePlan(input.plan);
    return {
      all: encodeDecimal(ledgerDebtBalance(plan, true)),
      unlinked: encodeDecimal(ledgerDebtBalance(plan, false)),
      countedInNetWorth: encodeDecimal(ledgerDebtBalanceCountedInNetWorth(plan)),
    };
  },

  'Plan.checkInBalances': (input) => {
    const plan = decodePlan(input.plan);
    const date = decodeDay(input.date);
    const ctx = contextFrom(input);
    const debtBalances = new Map((input.debtBalances ?? []).map((d) => [d.id, decodeDecimal(d.balance)] as const));
    const confirmed = { cash: decodeDecimal(input.cash), investments: decodeDecimal(input.investments), debtBalances };
    return {
      balances: encodeBalances(checkInBalances(plan, confirmed, date, ctx)),
      debts: checkInDebts(plan, debtBalances, date, ctx).map((d) => ({ id: d.debt.id, balance: encodeDecimal(d.balance) })),
    };
  },

  'Checkpoint.resolvingAssetValue': (input) => {
    const checkpoint = resolveCheckpoint(decodeCheckpoint(input.checkpoint), decodePlan(input.plan));
    return {
      confirmedBalances: encodeBalances(checkpoint.confirmedBalances),
      forecastedBalances: encodeBalances(checkpoint.forecastedBalances),
    };
  },

  'CashFlowChartBuilder.buildPoints': (input) =>
    buildChartPoints({
      transactions: decodeTransactions(input.transactions),
      checkpoints: (input.checkpoints ?? []).map(decodeCheckpoint),
      startingCash: decodeDecimal(input.startingCash),
      pastEvents: (input.pastEvents ?? []).map((e) => ({
        date: decodeDay(e.date),
        cashImpact: decodeDecimal(e.cashImpact),
        label: e.label,
        type: e.type,
      })),
      periodStart: decodeDay(input.periodStart),
      periodEnd: decodeDay(input.periodEnd),
      today: decodeDay(input.today),
    }).map((p) => ({
      date: formatDay(p.date),
      value: encodeDouble(p.value),
      isProjected: p.isProjected,
      transactions: p.transactions.map((t) => ({
        label: t.label,
        amount: encodeDecimal(t.amount),
        type: t.type,
        isWaypointAdjustment: t.isWaypointAdjustment,
      })),
    })),
};
